import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";
import { AttachButton, PendingAttachments, MessageAttachments, useTeamDrop, DropOverlay } from "@/components/TeamAttachments";
import { Hash, Send, Plus, Link2, X, MessagesSquare, Trash2, Lock, Settings2, Archive, RotateCcw } from "lucide-react";

const fmtTime = (iso) => (iso || "").slice(0, 16).replace("T", " ");

// highlight @Name mentions of known staff
function MessageBody({ text, staff, meId }) {
  const names = staff.map((s) => s.name).filter(Boolean).sort((a, b) => b.length - a.length);
  if (!names.length) return <>{text}</>;
  const esc = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(@(?:${esc.join("|")}|everyone|channel))`, "gi");
  const me = staff.find((s) => s.id === meId)?.name?.toLowerCase();
  return <>{text.split(re).map((part, i) => part.startsWith("@") && re.test(part)
    ? <span key={i} className={`font-semibold rounded px-0.5 ${part.slice(1).toLowerCase() === me ? "bg-amber-200 text-amber-900" : "text-primary"}`}>{part}</span>
    : <span key={i}>{part}</span>)}</>;
}

export default function TeamMessages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [channels, setChannels] = useState([]);
  const [staff, setStaff] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]);
  const [dmOpen, setDmOpen] = useState(false);
  const [newChanOpen, setNewChanOpen] = useState(false);
  const [newChan, setNewChan] = useState({ name: "", description: "", private: false, members: [] });
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({ name: "", description: "", members: [], private: false });
  const [archOpen, setArchOpen] = useState(false);
  const [archived, setArchived] = useState([]);
  const [clientQ, setClientQ] = useState("");
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [link, setLink] = useState({ client_id: "", application_id: "" });
  const [mentionQ, setMentionQ] = useState(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const activeId = params.get("c");
  const active = channels.find((c) => c.id === activeId);
  const drop = useTeamDrop(activeId, (f) => setPending((p) => [...p, f]));

  const loadChannels = () => api.get("/team/channels").then((r) => {
    setChannels(r.data);
    if (!params.get("c") && r.data.length) setParams({ c: r.data[0].id }, { replace: true });
  }).catch(() => {});
  useEffect(() => {
    loadChannels(); api.get("/team/staff").then((r) => setStaff(r.data)).catch(() => {});
    const t = setInterval(loadChannels, 15000); return () => clearInterval(t);
  }, []);

  const focusMsg = params.get("m");
  const flashed = useRef(null);
  const loadMsgs = (cid) => api.get(`/team/channels/${cid}/messages`, { params: { include: params.get("m") || undefined } }).then((r) => { setMsgs(r.data); window.dispatchEvent(new Event("team-read")); }).catch(() => setMsgs([]));
  useEffect(() => {
    if (!activeId) return;
    loadMsgs(activeId).then(loadChannels);
    const t = setInterval(() => loadMsgs(activeId), 5000); return () => clearInterval(t);
  }, [activeId]);
  useEffect(() => {
    if (focusMsg && flashed.current !== focusMsg) {
      const el = document.querySelector(`[data-testid="team-msg-${focusMsg}"]`);
      if (!el) return;
      flashed.current = focusMsg;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("target-flash"); setTimeout(() => el.classList.remove("target-flash"), 2700);
      return;
    }
    if (!focusMsg) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length, activeId, focusMsg, !!active]);

  useEffect(() => {
    if (!linkOpen || clients.length) return;
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [linkOpen]);
  useEffect(() => {
    setCases([]);
    if (link.client_id) api.get("/applications", { params: { client_id: link.client_id } }).then((r) => setCases(r.data)).catch(() => {});
  }, [link.client_id]);

  const mentionOptions = useMemo(() => mentionQ === null ? [] :
    [...staff.filter((s) => s.id !== user?.id).map((s) => s.name), "everyone"].filter((n) => n.toLowerCase().startsWith(mentionQ.toLowerCase())).slice(0, 6), [mentionQ, staff, user]);

  const onType = (v) => {
    setText(v);
    const m = /(^|\s)@([\w .'-]{0,30})$/.exec(v);
    setMentionQ(m ? m[2] : null);
  };
  const pickMention = (name) => {
    setText((t) => t.replace(/@([\w .'-]{0,30})$/, `@${name} `)); setMentionQ(null); inputRef.current?.focus();
  };

  const send = async () => {
    if ((!text.trim() && !pending.length) || !activeId) return;
    setSending(true);
    try {
      await api.post(`/team/channels/${activeId}/messages`, { body: text, client_id: link.client_id || null, application_id: link.application_id || null, attachments: pending.map((f) => f.id) });
      setText(""); setPending([]); setMentionQ(null); setLink({ client_id: "", application_id: "" }); setLinkOpen(false);
      await loadMsgs(activeId); loadChannels();
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };
  const openDm = async (uid) => {
    try { const r = await api.post("/team/dm", { user_id: uid }); setDmOpen(false); await loadChannels(); setParams({ c: r.data.id }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const createChannel = async () => {
    const name = newChan.name.trim();
    if (name.replace(/[^a-z0-9-]+/gi, "").length < 2) { toast.error("Channel name needs at least 2 letters, numbers or dashes"); return; }
    try {
      const r = await api.post("/team/channels", { name, description: newChan.description, private: newChan.private, members: newChan.members });
      setNewChanOpen(false); setNewChan({ name: "", description: "", private: false, members: [] });
      await loadChannels(); setParams({ c: r.data.id });
      toast.success("Channel created");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const deleteChannel = async (c) => {
    if (!window.confirm(`Delete #${c.name}? Members will no longer see it (messages stay on record).`)) return;
    try {
      await api.delete(`/team/channels/${c.id}`);
      toast.success(`#${c.name} deleted`);
      const r = await api.get("/team/channels"); setChannels(r.data);
      if (c.id === activeId) {
        const first = r.data.find((x) => x.kind === "channel") || r.data[0];
        setParams(first ? { c: first.id } : {}, { replace: true });
      }
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openEdit = () => {
    if (!active) return;
    setEdit({ name: active.name, description: active.description || "", members: active.members || [], private: !!active.private });
    setEditOpen((o) => !o);
  };
  const saveEdit = async () => {
    if (!active) return;
    try {
      const patch = { description: edit.description, private: edit.private };
      if (!active.protected) patch.name = edit.name;
      if (edit.private) patch.members = edit.members;
      await api.patch(`/team/channels/${active.id}`, patch);
      setEditOpen(false); await loadChannels();
      toast.success("Channel updated");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const loadArchived = () => api.get("/team/channels/archived").then((r) => setArchived(r.data)).catch((e) => toast.error(apiErr(e)));
  const openArchived = () => { loadArchived(); setArchOpen(true); };
  const restoreChannel = async (id) => {
    try {
      const r = await api.post(`/team/channels/${id}/restore`);
      toast.success(`#${r.data.name} restored`);
      await loadArchived(); await loadChannels(); setParams({ c: id });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const chanList = channels.filter((c) => c.kind === "channel");
  const dmList = channels.filter((c) => c.kind === "dm");
  const linkedLabel = link.application_id ? cases.find((a) => a.id === link.application_id)?.app_number : link.client_id ? clients.find((c) => c.id === link.client_id)?.full_name : "";

  const ChanBtn = ({ c }) => (
    <button data-testid={`team-channel-${c.kind === "channel" ? c.name : c.other_user_id}`} onClick={() => setParams({ c: c.id })}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${c.id === activeId ? "bg-primary text-white" : c.unread ? "text-slate-900 font-semibold hover:bg-slate-100" : "text-slate-600 hover:bg-slate-100"}`}>
      {c.kind === "channel" ? <Hash className="h-4 w-4 shrink-0" /> : <span className={`h-5 w-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${c.id === activeId ? "bg-white/20" : "bg-secondary text-white"}`}>{c.name?.[0]}</span>}
      <span className="truncate flex-1 text-left">{c.name}</span>
      {c.private && <Lock className={`h-3 w-3 shrink-0 ${c.id === activeId ? "text-white/70" : "text-slate-400"}`} aria-label="Private channel"><title>Private channel — only its members can see it</title></Lock>}
      {c.unread > 0 && c.id !== activeId && <span data-testid={`team-unread-${c.id}`} className="bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center">{c.unread}</span>}
    </button>
  );

  return (
    <div>
      <PageHeader title="Team Chat" subtitle="Internal team conversations — clients never see these." />
      <div className="grid md:grid-cols-[240px_1fr] gap-4">
        <Card className="p-3 space-y-4 md:h-[72vh] overflow-y-auto" data-testid="team-sidebar">
          <div>
            <div className="flex items-center justify-between px-2 mb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Channels</span>
              <button data-testid="team-new-channel" onClick={() => setNewChanOpen((o) => !o)} title="Create channel" className="text-slate-400 hover:text-primary"><Plus className="h-4 w-4" /></button>
            </div>
            {newChanOpen && (
              <div className="mb-2 border border-slate-200 rounded-lg p-2 space-y-2" data-testid="team-new-channel-form">
                <input data-testid="team-new-channel-name" value={newChan.name} onChange={(e) => setNewChan({ ...newChan, name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") createChannel(); }} placeholder="channel-name" className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm" />
                <input data-testid="team-new-channel-desc" value={newChan.description} onChange={(e) => setNewChan({ ...newChan, description: e.target.value })} placeholder="Description (optional)" className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm" />
                <label className="flex items-center gap-2 text-xs text-slate-600 px-0.5">
                  <input data-testid="team-new-channel-private" type="checkbox" checked={newChan.private} onChange={(e) => setNewChan({ ...newChan, private: e.target.checked, members: [] })} />
                  <Lock className="h-3 w-3" /> Private — choose who can see it
                </label>
                {newChan.private && (
                  <div className="max-h-32 overflow-y-auto border border-slate-100 rounded p-1 space-y-0.5" data-testid="team-new-channel-members">
                    {staff.filter((s) => s.id !== user?.id).map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-xs px-1 py-0.5 cursor-pointer">
                        <input type="checkbox" data-testid={`team-new-channel-member-${s.id}`} checked={newChan.members.includes(s.id)}
                          onChange={(e) => setNewChan((nc) => ({ ...nc, members: e.target.checked ? [...nc.members, s.id] : nc.members.filter((m) => m !== s.id) }))} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <button data-testid="team-new-channel-create" onClick={createChannel} className="flex-1 bg-primary text-white rounded py-1.5 text-sm">Create</button>
                  <button data-testid="team-new-channel-cancel" onClick={() => { setNewChanOpen(false); setNewChan({ name: "", description: "", private: false, members: [] }); }} className="px-3 rounded border border-slate-200 text-sm text-slate-500">Cancel</button>
                </div>
              </div>
            )}
            <div className="space-y-0.5">{chanList.map((c) => <ChanBtn key={c.id} c={c} />)}</div>
            {user?.role === "admin" && (
              <button data-testid="team-view-archived" onClick={openArchived}
                className="mt-1 w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <Archive className="h-3.5 w-3.5" /> Archived channels
              </button>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between px-2 mb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Direct messages</span>
              <button data-testid="team-new-dm" onClick={() => setDmOpen((o) => !o)} className="text-slate-400 hover:text-primary"><Plus className="h-4 w-4" /></button>
            </div>
            {dmOpen && (
              <div className="mb-2 border border-slate-200 rounded-lg p-1 max-h-48 overflow-y-auto" data-testid="team-dm-picker">
                {staff.filter((s) => s.id !== user?.id).map((s) => (
                  <button key={s.id} data-testid={`team-dm-pick-${s.id}`} onClick={() => openDm(s.id)} className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-slate-100">{s.name} <span className="text-[10px] text-slate-400 capitalize">{s.role}</span></button>
                ))}
              </div>
            )}
            <div className="space-y-0.5">{dmList.length ? dmList.map((c) => <ChanBtn key={c.id} c={c} />) : <div className="px-2 text-xs text-slate-400">No direct messages yet.</div>}</div>
          </div>
        </Card>

        <Card className="flex flex-col h-[72vh]" data-testid="team-conversation">
          {!active ? <div className="flex-1 flex items-center justify-center text-slate-400 text-sm"><MessagesSquare className="h-5 w-5 mr-2" /> Select a channel</div> : (
            <>
              <div className="px-5 py-3 border-b border-slate-100">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-head font-bold text-slate-900 flex items-center gap-1.5" data-testid="team-active-name">
                      {active.kind === "channel" ? <>{active.private && <Lock className="h-3.5 w-3.5 text-slate-400 shrink-0" />}#{active.name}</> : active.name}
                    </div>
                    {active.description && <div className="text-xs text-slate-400">{active.description}</div>}
                  </div>
                  {active.kind === "channel" && active.can_manage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button data-testid="team-edit-channel" onClick={openEdit} title="Edit channel" className="text-slate-400 hover:text-primary"><Settings2 className="h-4 w-4" /></button>
                      {!active.protected && <button data-testid="team-delete-channel" onClick={() => deleteChannel(active)} title="Delete channel" className="text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  )}
                </div>
                {editOpen && active.kind === "channel" && active.can_manage && (
                  <div className="mt-3 border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50" data-testid="team-edit-panel">
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Name</label>
                      <input data-testid="team-edit-name" value={edit.name} disabled={active.protected} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                        className="w-full mt-1 px-2 py-1.5 rounded border border-slate-200 text-sm disabled:bg-slate-100 disabled:text-slate-400" />
                      {active.protected && <div className="text-[11px] text-slate-400 mt-1">#general can't be renamed.</div>}
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Description</label>
                      <input data-testid="team-edit-desc" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                        className="w-full mt-1 px-2 py-1.5 rounded border border-slate-200 text-sm" placeholder="What's this channel for?" />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Members &amp; access</label>
                      {!active.protected && (
                        <label className="flex items-center gap-2 text-xs text-slate-600 mt-1 px-0.5 cursor-pointer">
                          <input type="checkbox" data-testid="team-edit-private" checked={edit.private}
                            onChange={(e) => setEdit((ed) => ({ ...ed, private: e.target.checked }))} />
                          <Lock className="h-3 w-3" /> Private — only chosen members can see it
                        </label>
                      )}
                      {edit.private ? (
                        <div className="mt-1 max-h-32 overflow-y-auto border border-slate-200 rounded p-1 space-y-0.5 bg-white" data-testid="team-edit-members">
                          {staff.filter((s) => s.id !== user?.id).map((s) => (
                            <label key={s.id} className="flex items-center gap-2 text-xs px-1 py-0.5 cursor-pointer">
                              <input type="checkbox" data-testid={`team-edit-member-${s.id}`} checked={edit.members.includes(s.id)}
                                onChange={(e) => setEdit((ed) => ({ ...ed, members: e.target.checked ? [...ed.members, s.id] : ed.members.filter((m) => m !== s.id) }))} />
                              {s.name}
                            </label>
                          ))}
                          <div className="text-[11px] text-slate-400 px-1 pt-1">You're always a member.</div>
                        </div>
                      ) : (
                        <div className="mt-1 max-h-32 overflow-y-auto border border-slate-200 rounded p-1 space-y-0.5 bg-slate-50" data-testid="team-edit-members">
                          {staff.map((s) => (
                            <label key={s.id} className="flex items-center gap-2 text-xs px-1 py-0.5 text-slate-500">
                              <input type="checkbox" data-testid={`team-edit-member-${s.id}`} checked disabled /> {s.name}{s.id === user?.id ? " (you)" : ""}
                            </label>
                          ))}
                          <div className="text-[11px] text-slate-400 px-1 pt-1">Public — everyone on the team is a member.{!active.protected && " Tick \u201CPrivate\u201D above to choose specific members."}</div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button data-testid="team-edit-save" onClick={saveEdit} className="bg-primary text-white rounded py-1.5 px-4 text-sm">Save</button>
                      <button data-testid="team-edit-cancel" onClick={() => setEditOpen(false)} className="px-3 rounded border border-slate-200 text-sm text-slate-500">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative flex-1 min-h-0 flex flex-col" data-testid="team-drop-zone" {...drop.dropProps}>
              <DropOverlay show={drop.dragging} />
              <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="team-messages">
                {msgs.length === 0 && <div className="text-center text-sm text-slate-400 py-10">No messages yet — say hello.</div>}
                {msgs.map((m) => (
                  <div key={m.id} className="flex gap-3" data-testid={`team-msg-${m.id}`}>
                    <div className="h-8 w-8 rounded-full bg-secondary text-white flex items-center justify-center text-xs font-bold shrink-0">{m.sender_name?.[0]}</div>
                    <div className="min-w-0">
                      <div className="text-xs"><b className="text-slate-800">{m.sender_name}</b> <span className="text-slate-400">{fmtTime(m.created_at)}</span></div>
                      {m.body && <div className="text-sm text-slate-700 whitespace-pre-wrap break-words"><MessageBody text={m.body} staff={staff} meId={user?.id} /></div>}
                      <MessageAttachments items={m.attachments} />
                      {(m.app_number || m.client_name) && (
                        <Link data-testid={`team-msg-link-${m.id}`} to={m.application_id ? `/admin/applications/${m.application_id}` : `/admin/clients/${m.client_id}`}
                          className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-primary bg-primary/5 rounded-full px-2 py-0.5 hover:bg-primary/10">
                          <Link2 className="h-3 w-3" /> {m.app_number ? `${m.app_number} · ${m.client_name || ""}` : m.client_name}
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="border-t border-slate-100 p-3 space-y-2 relative">
                {linkOpen && (
                  <div className="flex flex-wrap gap-2 items-center text-xs" data-testid="team-link-picker">
                    <span className="text-slate-500">Link to:</span>
                    <div className="relative">
                      <input data-testid="team-link-client" autoComplete="off"
                        value={link.client_id ? (clients.find((c) => c.id === link.client_id)?.full_name || "") : clientQ}
                        onChange={(e) => { setClientQ(e.target.value); setClientPickerOpen(true); if (link.client_id) setLink({ client_id: "", application_id: "" }); }}
                        onFocus={() => setClientPickerOpen(true)}
                        onBlur={() => setTimeout(() => setClientPickerOpen(false), 150)}
                        placeholder="Search client…" className="px-2 py-1.5 rounded-lg border border-slate-200 w-[200px]" />
                      {link.client_id && (
                        <button type="button" data-testid="team-link-client-clear" onMouseDown={(e) => { e.preventDefault(); setLink({ client_id: "", application_id: "" }); setClientQ(""); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
                      )}
                      {clientPickerOpen && (
                        <div className="absolute z-20 mt-1 w-[220px] max-h-52 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1" data-testid="team-link-client-list">
                          {clients.filter((c) => (c.full_name || "").toLowerCase().includes(clientQ.toLowerCase())).slice(0, 50).map((c) => (
                            <button key={c.id} type="button" data-testid={`team-link-client-opt-${c.id}`}
                              onMouseDown={(e) => { e.preventDefault(); setLink({ client_id: c.id, application_id: "" }); setClientQ(""); setClientPickerOpen(false); }}
                              className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100">{c.full_name}</button>
                          ))}
                          {clients.filter((c) => (c.full_name || "").toLowerCase().includes(clientQ.toLowerCase())).length === 0 && (
                            <div className="px-3 py-1.5 text-xs text-slate-400">No clients found</div>
                          )}
                        </div>
                      )}
                    </div>
                    {link.client_id && (
                      <select data-testid="team-link-case" value={link.application_id} onChange={(e) => setLink({ ...link, application_id: e.target.value })} className="px-2 py-1.5 rounded-lg border border-slate-200 max-w-[220px]">
                        <option value="">Whole client (no specific case)</option>{cases.map((a) => <option key={a.id} value={a.id}>{a.app_number} · {a.service_type}</option>)}
                      </select>
                    )}
                    <button onClick={() => { setLinkOpen(false); setLink({ client_id: "", application_id: "" }); }} className="text-slate-400"><X className="h-4 w-4" /></button>
                  </div>
                )}
                {mentionOptions.length > 0 && (
                  <div className="absolute bottom-full left-3 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-10 min-w-[180px]" data-testid="team-mention-list">
                    {mentionOptions.map((n) => <button key={n} data-testid={`team-mention-${n}`} onClick={() => pickMention(n)} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100">@{n}</button>)}
                  </div>
                )}
                <PendingAttachments files={pending} onRemove={(id) => setPending((p) => p.filter((f) => f.id !== id))} />
                <div className="flex gap-2">
                  <AttachButton channelId={activeId} onAdd={(f) => setPending((p) => [...p, f])} disabled={sending} busy={drop.busy} />
                  <button data-testid="team-link-toggle" onClick={() => setLinkOpen((o) => !o)} title="Link to a client or case"
                    className={`px-3 rounded-xl border text-sm inline-flex items-center gap-1 ${linkedLabel ? "border-primary text-primary bg-primary/5" : "border-slate-200 text-slate-500"}`}><Link2 className="h-4 w-4" />{linkedLabel && <span className="max-w-[120px] truncate text-xs">{linkedLabel}</span>}</button>
                  <input ref={inputRef} data-testid="team-input" value={text} onChange={(e) => onType(e.target.value)} onPaste={drop.onPaste}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (mentionOptions.length) pickMention(mentionOptions[0]); else send(); } }}
                    placeholder={`Message ${active.kind === "channel" ? `#${active.name}` : active.name} — type @ to mention, paste or drop files`} className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
                  <button data-testid="team-send" onClick={send} disabled={sending || (!text.trim() && !pending.length)} className="bg-primary text-white px-4 rounded-xl disabled:opacity-50"><Send className="h-4 w-4" /></button>
                </div>
              </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {archOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="team-archived-modal" onClick={() => setArchOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="font-head font-bold text-slate-900 flex items-center gap-2"><Archive className="h-4 w-4 text-slate-500" /> Archived channels</div>
              <button data-testid="team-archived-close" onClick={() => setArchOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto space-y-2">
              {archived.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-8">No archived channels. Deleted channels appear here and can be restored.</div>
              ) : archived.map((a) => (
                <div key={a.id} data-testid={`team-archived-${a.id}`} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">{a.private && <Lock className="h-3 w-3 text-slate-400" />}#{a.name}</div>
                    <div className="text-[11px] text-slate-400 truncate">{a.messages} message{a.messages === 1 ? "" : "s"} · deleted by {a.archived_by} · {fmtTime(a.archived_at)}</div>
                  </div>
                  <button data-testid={`team-restore-${a.id}`} onClick={() => restoreChannel(a.id)}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/5 hover:bg-primary/10 rounded-lg px-3 py-1.5">
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
